import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    PaperPlaneTiltIcon,
    ImageIcon,
    GifIcon,
    SmileyIcon,
    XIcon,
    ArrowBendUpLeftIcon,
    DotsThreeIcon,
    TrashIcon
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { ChatService, ChatMessage, TenorService, ReactionCount } from '../../lib/chatService';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';
import { useChatStore } from '../../stores/chatStore';
import { chatEventBus } from '../../lib/chatEventBus';

interface ChatPanelProps {
    projectId: string;
    className?: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ projectId, className = '' }) => {
    const { user } = useAuth();
    const { getPendingMessages, clearPendingMessages } = useChatStore();
    const storeVersion = useChatStore(state => state.version);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);

    // Pickers
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Typing indicators
    const [typingUsers, setTypingUsers] = useState<string[]>([]);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastTypingBroadcast = useRef<number>(0);

    // Refs
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Channel ref for cleanup
    const channelRef = useRef<any>(null);

    // Scroll to bottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Load initial messages
    useEffect(() => {
        const loadMessages = async () => {
            setLoading(true);
            const msgs = await ChatService.getMessages(projectId);
            setMessages(msgs);
            setLoading(false);
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        };
        loadMessages();
    }, [projectId]);

    // Check for pending messages from chat store (messages sent while chat was closed)
    useEffect(() => {
        const pending = getPendingMessages(projectId);
        if (pending.length > 0) {
            console.log('📬 ChatPanel: Adding', pending.length, 'pending messages from store');
            setMessages(prev => {
                // Filter out any duplicates by ID
                const existingIds = new Set(prev.map(m => m.id));
                const newMessages = pending.filter(m => !existingIds.has(m.id));
                if (newMessages.length > 0) {
                    setTimeout(() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 50);
                    return [...prev, ...newMessages];
                }
                return prev;
            });
            clearPendingMessages(projectId);
        }
    }, [projectId, storeVersion, getPendingMessages, clearPendingMessages]);

    // Real-time subscription
    useEffect(() => {
        if (!projectId || !user) return;

        const channel = supabase
            .channel(`project_chat:${projectId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages',
                    filter: `project_id=eq.${projectId}`
                },
                async (payload) => {
                    console.log('📨 Chat subscription: New message event', payload.new.id);
                    try {
                        const message = await ChatService.getMessage(payload.new.id);
                        if (message) {
                            console.log('✅ Chat subscription: Fetched message', message.id, message.message_type);
                            // Check if message already exists (from optimistic update)
                            setMessages(prev => {
                                const exists = prev.some(m => m.id === message.id);
                                if (exists) {
                                    console.log('🔄 Chat subscription: Replacing existing message');
                                    // Replace temp/existing message with real one
                                    return prev.map(m => m.id === message.id ? message : m);
                                }
                                console.log('➕ Chat subscription: Adding new message');
                                return [...prev, message];
                            });
                            setTimeout(() => {
                                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }, 50);
                        } else {
                            console.warn('⚠️ Chat subscription: getMessage returned null for', payload.new.id);
                        }
                    } catch (error) {
                        console.error('❌ Chat subscription: Error handling message', error);
                    }
                }
            )
            .on('broadcast', { event: 'typing' }, (payload) => {
                const { user_id, username } = payload.payload;
                if (user_id === user.id) return;

                setTypingUsers(prev => {
                    if (!prev.includes(username)) {
                        return [...prev, username];
                    }
                    return prev;
                });

                // Clear typing after 3 seconds
                setTimeout(() => {
                    setTypingUsers(prev => prev.filter(u => u !== username));
                }, 3000);
            })
            .on('broadcast', { event: 'new_message' }, async (payload) => {
                const { messageId } = payload.payload;
                console.log('📡 Received broadcast for new message:', messageId);

                // Fetch the message and add it to the chat
                try {
                    const message = await ChatService.getMessage(messageId);
                    if (message) {
                        console.log('✅ Fetched broadcasted message:', message.id);
                        setMessages(prev => {
                            const exists = prev.some(m => m.id === message.id);
                            if (exists) {
                                console.log('🔄 Message already exists, skipping');
                                return prev;
                            }
                            console.log('➕ Adding broadcasted message to chat');
                            return [...prev, message];
                        });
                        setTimeout(() => {
                            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 50);
                    }
                } catch (error) {
                    console.error('❌ Error fetching broadcasted message:', error);
                }
            })
            .subscribe((status) => {
                console.log('🔌 Chat subscription status:', status, 'for project:', projectId);
            });

        channelRef.current = channel;

        return () => {
            console.log('🔌 Chat subscription: Unsubscribing from project:', projectId);
            supabase.removeChannel(channel);
        };
    }, [projectId, user]); // Removed scrollToBottom to prevent unnecessary re-subscriptions

    // Subscribe to chat event bus for immediate updates
    useEffect(() => {
        const unsubscribe = chatEventBus.subscribe((eventProjectId, message) => {
            // Only handle messages for this project
            if (eventProjectId !== projectId) return;

            // Add message immediately - no fetching needed
            setMessages(prev => {
                const exists = prev.some(m => m.id === message.id);
                if (!exists) {
                    return [...prev, message];
                }
                return prev;
            });
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
        });

        return unsubscribe;
    }, [projectId]);

    // Broadcast typing indicator (debounced)
    const broadcastTyping = useCallback(() => {
        if (!channelRef.current || !user) return;

        const now = Date.now();
        if (now - lastTypingBroadcast.current < 2000) return; // Debounce 2 seconds

        lastTypingBroadcast.current = now;
        channelRef.current.send({
            type: 'broadcast',
            event: 'typing',
            payload: { user_id: user.id, username: user.username || 'User' }
        });
    }, [user]);

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewMessage(e.target.value);
        broadcastTyping();
    };

    // Send message
    const handleSend = async () => {
        if (!newMessage.trim() || sending) return;

        setSending(true);
        const content = newMessage.trim();
        setNewMessage('');

        // Optimistically add message
        const tempMessage: ChatMessage = {
            id: `temp-${Date.now()}`,
            project_id: projectId,
            user_id: user?.id || '',
            content,
            message_type: 'text',
            attachment_url: null,
            reply_to_id: replyTo?.id || null,
            is_deleted: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user: {
                id: user?.id || '',
                username: user?.username || 'You',
                full_name: user?.full_name,
                avatar_url: user?.avatar_url
            }
        };
        setMessages(prev => [...prev, tempMessage]);
        setReplyTo(null);
        scrollToBottom();

        const message = await ChatService.sendMessage(
            projectId,
            content,
            'text',
            undefined,
            replyTo?.id
        );

        if (message) {
            // Replace temp message with real one
            setMessages(prev =>
                prev.map(m => m.id === tempMessage.id ? message : m)
            );
        } else {
            // Remove temp message on error
            setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
        }

        setSending(false);
        inputRef.current?.focus();
    };

    // Handle key press
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Send GIF
    const handleGifSelect = async (gifUrl: string) => {
        setShowGifPicker(false);
        setSending(true);

        // Optimistic update
        const tempMessage: ChatMessage = {
            id: `temp-${Date.now()}`,
            project_id: projectId,
            user_id: user?.id || '',
            content: '',
            message_type: 'gif',
            attachment_url: gifUrl,
            reply_to_id: null,
            is_deleted: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user: {
                id: user?.id || '',
                username: user?.username || 'You',
                full_name: user?.full_name,
                avatar_url: user?.avatar_url
            }
        };
        setMessages(prev => [...prev, tempMessage]);
        scrollToBottom();

        const message = await ChatService.sendMessage(projectId, '', 'gif', gifUrl);
        if (message) {
            // Replace temp message with real one
            setMessages(prev => prev.map(m => m.id === tempMessage.id ? message : m));
        } else {
            // Remove temp message on error
            setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
        }
        setSending(false);
    };

    // Send image (Discord limit: 25MB)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Clear previous errors
        setUploadError(null);

        // Check file size (Discord limit: 25MB)
        if (file.size > MAX_FILE_SIZE) {
            setUploadError(`File too large. Maximum size is 25MB (yours: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
            // Clear the input so user can try again
            if (fileInputRef.current) fileInputRef.current.value = '';
            // Auto-dismiss error after 5s
            setTimeout(() => setUploadError(null), 5000);
            return;
        }

        setSending(true);
        const url = await ChatService.uploadChatImage(file, projectId);
        if (url) {
            // Optimistic update
            const tempMessage: ChatMessage = {
                id: `temp-${Date.now()}`,
                project_id: projectId,
                user_id: user?.id || '',
                content: '',
                message_type: 'image',
                attachment_url: url,
                reply_to_id: null,
                is_deleted: false,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                user: {
                    id: user?.id || '',
                    username: user?.username || 'You',
                    full_name: user?.full_name,
                    avatar_url: user?.avatar_url
                }
            };
            setMessages(prev => [...prev, tempMessage]);
            scrollToBottom();

            const message = await ChatService.sendMessage(projectId, '', 'image', url);
            if (message) {
                // Replace temp message with real one
                setMessages(prev => prev.map(m => m.id === tempMessage.id ? message : m));
            } else {
                // Remove temp message on error
                setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
            }
        }
        setSending(false);
        // Clear the input
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Add emoji to message
    const handleEmojiSelect = (emoji: string) => {
        setNewMessage(prev => prev + emoji);
        setShowEmojiPicker(false);
        inputRef.current?.focus();
    };

    // Toggle reaction on message
    const handleReaction = async (messageId: string, emoji: string) => {
        setShowReactionPicker(null);
        await ChatService.toggleReaction(messageId, emoji);
        // Refetch message reactions - in production you'd use realtime
        const updated = await ChatService.getMessage(messageId);
        if (updated) {
            setMessages(prev => prev.map(m => m.id === messageId ? updated : m));
        }
    };

    // Delete message
    const handleDelete = async (messageId: string) => {
        await ChatService.deleteMessage(messageId);
        setMessages(prev => prev.filter(m => m.id !== messageId));
    };

    // Group reactions by emoji
    const getReactionGroups = (reactions: any[]): ReactionCount[] => {
        if (!reactions) return [];
        const groups: Map<string, ReactionCount> = new Map();

        reactions.forEach((r: any) => {
            const existing = groups.get(r.emoji);
            if (existing) {
                existing.count++;
                existing.users.push({ id: r.user_id, username: r.user?.username || '' });
                if (r.user_id === user?.id) existing.hasReacted = true;
            } else {
                groups.set(r.emoji, {
                    emoji: r.emoji,
                    count: 1,
                    users: [{ id: r.user_id, username: r.user?.username || '' }],
                    hasReacted: r.user_id === user?.id
                });
            }
        });

        return Array.from(groups.values());
    };

    return (
        <div className={`flex flex-col h-full bg-onyx ${className}`}>
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1 custom-scrollbar">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                        <SmileyIcon className="w-12 h-12 mb-2 text-zinc-700" />
                        <p className="text-sm">No messages yet</p>
                        <p className="text-xs text-zinc-600">Start the conversation!</p>
                    </div>
                ) : (
                    messages.map((message, index) => {
                        // Group consecutive messages from the same user (Discord-style)
                        const showAvatar = index === 0 ||
                            messages[index - 1].user_id !== message.user_id;

                        return (
                            <MessageBubble
                                key={message.id}
                                message={message}
                                showAvatar={showAvatar}
                                isOwn={message.user_id === user?.id}
                                onReply={() => setReplyTo(message)}
                                onReact={(emoji) => handleReaction(message.id, emoji)}
                                onDelete={() => handleDelete(message.id)}
                                reactionGroups={getReactionGroups(message.reactions || [])}
                                showReactionPicker={showReactionPicker === message.id}
                                setShowReactionPicker={(show) => setShowReactionPicker(show ? message.id : null)}
                            />
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
                <div className="px-4 py-1 text-xs text-zinc-500 italic">
                    {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
            )}

            {/* Reply Preview */}
            {replyTo && (
                <div className="mx-4 px-3 py-2 bg-zinc-800/50 rounded-t-lg border-l-2 border-indigo-500 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-indigo-400">Replying to @{replyTo.user?.username}</p>
                        <p className="text-xs text-zinc-400 truncate">{replyTo.content}</p>
                    </div>
                    <button
                        onClick={() => setReplyTo(null)}
                        className="p-1 text-zinc-500 hover:text-white"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Upload Error */}
            {uploadError && (
                <div className="mx-4 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center justify-between">
                    <p className="text-xs text-red-400">{uploadError}</p>
                    <button
                        onClick={() => setUploadError(null)}
                        className="p-1 text-red-400 hover:text-red-300"
                    >
                        <XIcon className="w-3 h-3" />
                    </button>
                </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-white/5">
                <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2">
                    {/* GIF Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowGifPicker(!showGifPicker)}
                            className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                            title="Send GIF"
                        >
                            <GifIcon className="w-5 h-5" />
                        </button>
                        {showGifPicker && (
                            <GifPicker
                                onSelect={handleGifSelect}
                                onClose={() => setShowGifPicker(false)}
                            />
                        )}
                    </div>

                    {/* Image Upload */}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                        title="Upload Image"
                    >
                        <ImageIcon className="w-5 h-5" />
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                    />

                    {/* Message Input */}
                    <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        placeholder="Type a message..."
                        className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none"
                    />

                    {/* Emoji Picker */}
                    <div className="relative">
                        <button
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                            title="Add Emoji"
                        >
                            <SmileyIcon className="w-5 h-5" />
                        </button>
                        {showEmojiPicker && (
                            <EmojiPicker
                                onSelect={handleEmojiSelect}
                                onClose={() => setShowEmojiPicker(false)}
                            />
                        )}
                    </div>

                    {/* Send Button */}
                    <button
                        onClick={handleSend}
                        disabled={!newMessage.trim() || sending}
                        className="p-1.5 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PaperPlaneTiltIcon className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Message Bubble Component
interface MessageBubbleProps {
    message: ChatMessage;
    showAvatar: boolean;
    isOwn: boolean;
    onReply: () => void;
    onReact: (emoji: string) => void;
    onDelete: () => void;
    reactionGroups: ReactionCount[];
    showReactionPicker: boolean;
    setShowReactionPicker: (show: boolean) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    showAvatar,
    isOwn,
    onReply,
    onReact,
    onDelete,
    reactionGroups,
    showReactionPicker,
    setShowReactionPicker
}) => {
    const [showActions, setShowActions] = useState(false);

    const quickReactions = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

    // Format time for hover timestamp (Discord shows "HH:MM" on hover for consecutive messages)
    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div
            className={`group relative hover:bg-white/[0.02] ${showAvatar ? 'mt-4 pt-0.5' : 'py-[1px]'}`}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            <div className="flex items-start gap-3 px-1">
                {/* Avatar or Hover Timestamp */}
                {showAvatar ? (
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex-shrink-0 overflow-hidden">
                        {message.user?.avatar_url ? (
                            <img src={message.user.avatar_url} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm font-bold">
                                {(message.user?.full_name || message.user?.username)?.[0]?.toUpperCase() || '?'}
                            </div>
                        )}
                    </div>
                ) : (
                    // Show timestamp on hover for consecutive messages (Discord style)
                    <div className="w-10 flex-shrink-0 flex items-center justify-center h-[22px]">
                        <span className="text-[10px] text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            {formatTime(new Date(message.created_at))}
                        </span>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Header - only for first message in group */}
                    {showAvatar && (
                        <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium text-white hover:underline cursor-pointer">
                                {message.user?.full_name || message.user?.username || 'Unknown'}
                            </span>
                            <span className="text-[10px] text-zinc-600">
                                {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                            </span>
                        </div>
                    )}

                    {/* Message Content */}
                    {message.message_type === 'text' && (
                        <p className={`text-sm text-zinc-300 break-words leading-[22px] ${showAvatar ? 'mt-0.5' : ''}`}>
                            {message.content}
                        </p>
                    )}

                    {message.message_type === 'image' && message.attachment_url && (
                        <img
                            src={message.attachment_url}
                            alt="Uploaded"
                            className="max-w-xs rounded-lg mt-1 cursor-pointer hover:opacity-90"
                            onClick={() => window.open(message.attachment_url!, '_blank')}
                        />
                    )}

                    {message.message_type === 'gif' && message.attachment_url && (
                        <img
                            src={message.attachment_url}
                            alt="GIF"
                            className="max-w-xs rounded-lg mt-1"
                        />
                    )}

                    {message.message_type === 'system' && (
                        <p className="text-xs text-zinc-500 italic">{message.content}</p>
                    )}

                    {/* Reactions */}
                    {reactionGroups.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                            {reactionGroups.map(group => (
                                <button
                                    key={group.emoji}
                                    onClick={() => onReact(group.emoji)}
                                    className={`
                                        flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs
                                        ${group.hasReacted
                                            ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                            : 'bg-zinc-800 text-zinc-400 border border-transparent hover:border-zinc-700'
                                        }
                                    `}
                                    title={group.users.map(u => u.username).join(', ')}
                                >
                                    <span>{group.emoji}</span>
                                    <span>{group.count}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Hover Actions */}
            {showActions && (
                <div className="absolute -top-3 right-2 flex items-center gap-0.5 bg-zinc-800 rounded-lg border border-zinc-700 shadow-lg z-10">
                    {/* Quick Reactions */}
                    {quickReactions.map(emoji => (
                        <button
                            key={emoji}
                            onClick={() => onReact(emoji)}
                            className="p-1.5 hover:bg-white/10 text-sm"
                        >
                            {emoji}
                        </button>
                    ))}

                    {/* Reply */}
                    <button
                        onClick={onReply}
                        className="p-1.5 hover:bg-white/10 text-zinc-400 hover:text-white"
                        title="Reply"
                    >
                        <ArrowBendUpLeftIcon className="w-4 h-4" />
                    </button>

                    {/* Delete (own messages only) */}
                    {isOwn && (
                        <button
                            onClick={onDelete}
                            className="p-1.5 hover:bg-red-500/10 text-zinc-400 hover:text-red-400"
                            title="Delete"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChatPanel;
